import React, { useEffect, useState, useRef } from "react"
import { motion, useInView, useMotionValue, useSpring } from "framer-motion"
import { cn } from "../../utils/cn"

// Custom hook for counting animation
const useCountAnimation = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start)
  const motionValue = useMotionValue(start)
  const springValue = useSpring(motionValue, {
    damping: 100,
    stiffness: 100
  })

  useEffect(() => {
    const unsubscribe = springValue.onChange((value) => {
      setCount(Math.round(value))
    })

    return unsubscribe
  }, [springValue])

  const startAnimation = () => {
    motionValue.set(end)
  }

  return { count, startAnimation }
}

const MetricCard = ({ title, value, suffix = "", icon: Icon, delay = 0, description }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  // A null value means the metric is genuinely unavailable (the API did not
  // return it). Count-up needs a real number, so pass 0 to keep the spring
  // hook happy and render a dash instead of animating a fabricated figure —
  // see the display branch below.
  const hasValue = Number.isFinite(value)
  const { count, startAnimation } = useCountAnimation(hasValue ? value : 0)

  useEffect(() => {
    if (isInView && hasValue) {
      const timer = setTimeout(() => {
        startAnimation()
      }, delay)
      return () => clearTimeout(timer)
    }
  }, [isInView, hasValue, startAnimation, delay])

  return (
    <motion.div
      ref={ref}
      className="relative group"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: delay / 1000 }}
      whileHover={{ y: -4, transition: { duration: 0.3 } }}
    >
      <div className="relative bg-white rounded-2xl p-6 border border-gray-200/80 shadow-elev-1 hover:shadow-elev-2 transition-all duration-300 overflow-hidden">
        <div className="relative z-10">
          {/* Icon */}
          <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mb-4 mx-auto">
            <Icon className="w-6 h-6 text-white" />
          </div>

          {/* Value */}
          <motion.div
            className="text-center mb-2"
            animate={isInView ? { scale: [0.8, 1.1, 1] } : { scale: 0.8 }}
            transition={{ duration: 0.8, delay: delay / 1000 + 0.3 }}
          >
            <span className="text-3xl md:text-4xl font-bold text-gray-800">
              {hasValue ? count : '—'}
              {hasValue && <span className="text-primary-600">{suffix}</span>}
            </span>
          </motion.div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-800 text-center mb-2">
            {title}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-600 text-center leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </motion.div>
  )
}

export const AnimatedMetrics = ({ metrics, className }) => {
  const containerRef = useRef(null)
  const isInView = useInView(containerRef, { once: false, margin: "-50px" })

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Section background with animated elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated grid */}
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={isInView ? { opacity: [0.05, 0.15, 0.05] } : { opacity: 0.05 }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          <svg className="w-full h-full">
            <defs>
              <pattern
                id="metrics-grid"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 60 0 L 0 0 0 60"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  className="text-primary-300"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#metrics-grid)" />
          </svg>
        </motion.div>
      </div>

      {/* Header */}
      <motion.div
        className="text-center mb-12 relative z-10"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 mb-4">
          System Impact
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Real-time insights from our integrated early warning system helping communities
          prepare for climate-related health challenges
        </p>
      </motion.div>

      {/* Metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
        {metrics.map((metric, index) => (
          <MetricCard
            key={metric.title}
            {...metric}
            delay={index * 200}
          />
        ))}
      </div>

    </div>
  )
}

export default AnimatedMetrics